import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Buildings, ChatDots, ArrowRight, Stars, DoorClosedFill, BoxArrowUpRight } from 'react-bootstrap-icons';
import { useLocale } from '../../i18n/LocaleContext';
import { useAuth } from '../../auth/AuthContext';
import {
  fetchBrandBuildingInfo,
  formatHuf,
  resolveStorefront,
  updateStorefrontLogoShape,
  BRAND_BUILDING_PRICE_FALLBACK,
  LOGO_SHAPES,
} from '../../api/brandBuildings';
import { fetchBrand, shopifyConnectUrl } from '../../api/brands';
import { describeConnection } from '../shopifyConnection';
import BuildingViewer from './BuildingViewer';
import StyleCategoryPicker from './StyleCategoryPicker';
import ConfirmDialog from '../ConfirmDialog';
import './BrandBuilding.scss';

export const BRAND_BUILDING_PATH = '/dashboard/brand-building';
export const SUPPORT_PATH = '/dashboard/support';

// Profile tab: "this is how your brand looks in Outfino". A brand with a live
// custom building sees that building, with a "Redesign" action behind a
// confirm; everyone else sees the generic store with their own logo on the
// sign and the way to ask for a custom one.
// `canRequest`: the building endpoints need the editBrand permission (owner
// or a member with it); others still see the preview, without the actions.
//
// Default store only (no live custom building): the brand picks the logo
// plaque's shape — round / square / wide rectangle — saved as
// storefrontLogoShape (DEFAULT_STORE_CONTRACT.md). A store the map shows
// closed (storefrontClosed: inactive brand / no Shopify connection) gets a
// notice with the fix, and the preview starts in the closed state with an
// open/closed toggle.
//
// Below the preview, for every store: the style category — which galaxy of
// the map the store stands in (StyleCategoryPicker).
export default function BrandBuildingSection({ brand, canRequest = true }) {
  const { t, lang } = useLocale();
  const { embedded } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null); // null = loading
  // GET /brands/:id — the only partner payload with the closed state
  // (storefrontClosed / storefrontClosedReason); null until it answers.
  const [detail, setDetail] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Logo shape: `pendingShape` is the optimistic choice; null = the stored one.
  const [pendingShape, setPendingShape] = useState(null);
  const [shapeStatus, setShapeStatus] = useState('idle'); // idle | saving | saved | error
  const shapeSeq = useRef(0);
  const savedShape = useRef(null);
  // Open/closed preview: null = follow the map's real state.
  const [previewClosed, setPreviewClosed] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchBrandBuildingInfo(brand.id)
      .then((res) => alive && setInfo(res))
      // Not deployed yet / embedded / offline: the generic store is still the
      // truth for a brand we know nothing more about.
      .catch(() => alive && setInfo({ building: null, priceFromHuf: BRAND_BUILDING_PRICE_FALLBACK, openThread: null, failed: true }));
    return () => {
      alive = false;
    };
  }, [brand.id]);

  // Re-read when the brand's visibility or Shopify link changes (the profile
  // save and the shell's refresh update the /user/me brand).
  const connStatus = brand.shopifyConnection?.status || null;
  useEffect(() => {
    let alive = true;
    fetchBrand(brand.id)
      .then((res) => alive && setDetail(res || null))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [brand.id, brand.isActive, connStatus]);

  const building = info?.building || null;
  const price = formatHuf(info?.priceFromHuf || BRAND_BUILDING_PRICE_FALLBACK, lang);
  const cover = brand.coverImage || brand.cover_image || null;
  const closeConfirm = useCallback(() => setConfirmOpen(false), []);

  const storefront = resolveStorefront(brand, info, detail);
  const storedShape = savedShape.current || storefront.logoShape;
  const logoShape = pendingShape || storedShape;
  const showClosed = previewClosed ?? storefront.closed;

  // A different brand starts from its own stored state.
  useEffect(() => {
    shapeSeq.current += 1;
    savedShape.current = null;
    setPendingShape(null);
    setShapeStatus('idle');
    setPreviewClosed(null);
  }, [brand.id]);

  const chooseShape = async (next) => {
    if (!canRequest || next === logoShape) return;
    const seq = ++shapeSeq.current;
    const rollbackTo = storedShape;
    setPendingShape(next);
    setShapeStatus('saving');
    try {
      const saved = await updateStorefrontLogoShape(brand.id, next);
      if (seq !== shapeSeq.current) return;
      savedShape.current = saved;
      setPendingShape(null);
      setShapeStatus('saved');
    } catch {
      if (seq !== shapeSeq.current) return;
      savedShape.current = rollbackTo;
      setPendingShape(null);
      setShapeStatus('error');
    }
  };

  return (
    <section className="panel-card bb-section" aria-labelledby="bb-section-title">
      <div className="bb-section__grid">
        <div className="bb-section__media">
          {info ? (
            <div className="bb-section__stage">
              <BuildingViewer
                key={building ? `${building.lod0Url}:${building.version}` : 'generic'}
                className="bb-section__viewer"
                modelUrl={building?.lod0Url || null}
                logoUrl={brand.logo || building?.brandLogoUrl || null}
                coverUrl={cover}
                brandName={brand.name}
                fallbackImage={building?.thumbnailUrl}
                logoShape={logoShape}
                closed={showClosed}
              />
              {storefront.closed && (
                <OpenClosedToggle closed={showClosed} onChange={setPreviewClosed} t={t} />
              )}
            </div>
          ) : (
            <div className="store-viewer bb-section__viewer" aria-busy="true">
              <div className="store-viewer__status">
                <span className="store-viewer__spinner" aria-hidden="true" />
              </div>
            </div>
          )}
        </div>

        <div className="bb-section__aside">
          <span className={`bb-badge${building ? ' bb-badge--custom' : ''}`}>
            {building ? <Stars size={13} /> : <Buildings size={13} />}
            {building ? t('building.badgeCustom') : t('building.badgeGeneric')}
          </span>
          <h2 className="panel-card__title bb-section__title" id="bb-section-title">
            {t('building.sectionTitle')}
          </h2>
          <p className="bb-section__lead">
            {building ? t('building.sectionLeadCustom') : t('building.sectionLeadGeneric')}
          </p>

          {info && storefront.closed && (
            <ClosedNotice brand={brand} reason={storefront.closedReason} embedded={embedded} t={t} />
          )}

          {info && !building && (
            <LogoShapePicker
              value={logoShape}
              status={shapeStatus}
              canEdit={canRequest}
              onChange={chooseShape}
              t={t}
            />
          )}

          {!embedded && canRequest && info?.openThread && (
            <Link className="bb-thread" to={`${SUPPORT_PATH}/${info.openThread.id}`}>
              <span className="bb-thread__icon"><ChatDots size={16} /></span>
              <span className="bb-thread__text">
                <strong>{t('building.openThreadTitle')}</strong>
                <span>{t('building.openThreadBody')}</span>
              </span>
              <ArrowRight className="bb-thread__arrow" size={16} />
            </Link>
          )}

          <div className="bb-section__foot">
            <p className="bb-section__price">
              {t('building.priceNoteLabel')}{' '}
              <strong>{t('building.priceFrom', { price })}</strong>
            </p>
            {!embedded && canRequest && (
              building ? (
                <button type="button" className="btn-o btn-o--ghost" onClick={() => setConfirmOpen(true)}>
                  {t('building.redesign')}
                </button>
              ) : (
                <button type="button" className="btn-o btn-o--brand bb-section__cta" onClick={() => navigate(BRAND_BUILDING_PATH)}>
                  {t('building.cta')}
                </button>
              )
            )}
          </div>
        </div>
      </div>

      {/* Which galaxy the store stands in — for every store, default or
          custom building alike. */}
      <StyleCategoryPicker brand={brand} detail={detail} canEdit={canRequest} />

      <ConfirmDialog
        open={confirmOpen}
        title={t('building.redesignConfirmTitle')}
        body={t('building.redesignConfirmBody')}
        cancelLabel={t('building.redesignCancel')}
        confirmLabel={t('building.redesignConfirm')}
        onCancel={closeConfirm}
        onConfirm={() => {
          setConfirmOpen(false);
          navigate(`${BRAND_BUILDING_PATH}?redesign=1#request`);
        }}
      />
    </section>
  );
}

// ── default-store logo shape ─────────────────────────────────────────────────

/// Tiny outline of each plaque, drawn the way the store shows it.
function ShapeIcon({ shape }) {
  return (
    <svg className="bb-shape__icon" width="22" height="16" viewBox="0 0 22 16" aria-hidden="true" focusable="false">
      {shape === 'round' && <circle cx="11" cy="8" r="6" />}
      {shape === 'square' && <rect x="5" y="2" width="12" height="12" rx="1.6" />}
      {shape === 'rect' && <rect x="1.5" y="4.5" width="19" height="7" rx="1.2" />}
    </svg>
  );
}

function LogoShapePicker({ value, status, canEdit, onChange, t }) {
  const note =
    !canEdit ? t('building.logoShapeReadOnly')
      : status === 'saving' ? t('building.logoShapeSaving')
        : status === 'saved' ? t('building.logoShapeSaved')
          : status === 'error' ? t('building.logoShapeError')
            : t('building.logoShapeHint');
  return (
    <div className="bb-shape">
      <span className="bb-shape__label" id="bb-shape-label">{t('building.logoShapeLabel')}</span>
      <div className="bb-seg" role="radiogroup" aria-labelledby="bb-shape-label" aria-describedby="bb-shape-note">
        {LOGO_SHAPES.map((shape) => (
          <button
            key={shape}
            type="button"
            role="radio"
            aria-checked={value === shape}
            className={`bb-seg__btn${value === shape ? ' is-on' : ''}`}
            disabled={!canEdit}
            onClick={() => onChange(shape)}
          >
            <ShapeIcon shape={shape} />
            <span>{t(`building.logoShape.${shape}`)}</span>
          </button>
        ))}
      </div>
      <span
        id="bb-shape-note"
        className={`bb-shape__note${status === 'error' && canEdit ? ' is-error' : ''}`}
        role={status === 'error' ? 'alert' : undefined}
        aria-live="polite"
      >
        {note}
      </span>
    </div>
  );
}

// ── closed storefront ────────────────────────────────────────────────────────

function ClosedNotice({ brand, reason, embedded, t }) {
  const body =
    reason === 'inactive' ? t('building.closedInactive')
      : reason === 'no_shopify' ? t('building.closedNoShopify')
        : t('building.closedGeneric');
  const hint =
    reason === 'inactive' ? t('building.closedHintInactive')
      : reason === 'no_shopify' ? t('building.closedHintNoShopify')
        : null;
  // Same fix as the connection banner: the Outfino app in the Shopify admin,
  // or (portal only) the connect flow scoped to this shop.
  let href = null;
  if (reason === 'no_shopify') {
    href = describeConnection(brand.shopifyConnection).reauthUrl
      || (!embedded && brand.website ? shopifyConnectUrl(brand.website) : null);
  }
  const external = href && !href.includes('/shopify/auth?');
  return (
    <div className="bb-closed" role="status">
      <span className="bb-closed__icon" aria-hidden="true"><DoorClosedFill size={15} /></span>
      <div className="bb-closed__text">
        <strong>{body}</strong>
        {hint && <span>{hint}</span>}
        {href && (
          <a className="bb-closed__cta" href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
            {t('building.closedCtaShopify')}
            {external && <BoxArrowUpRight size={12} aria-hidden="true" />}
          </a>
        )}
      </div>
    </div>
  );
}

function OpenClosedToggle({ closed, onChange, t }) {
  return (
    <div className="bb-oc" role="group" aria-label={t('building.previewToggle')}>
      <button type="button" className={`bb-oc__btn${!closed ? ' is-on' : ''}`} aria-pressed={!closed} onClick={() => onChange(false)}>
        {t('building.previewOpen')}
      </button>
      <button type="button" className={`bb-oc__btn${closed ? ' is-on' : ''}`} aria-pressed={closed} onClick={() => onChange(true)}>
        {t('building.previewClosed')}
      </button>
    </div>
  );
}
